import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy, useBusyKeys } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { getBracketDetail, deleteBracket } from '../../api/client';
import api from '../../api/client';
import { apiError } from '../../hooks/useApiCall';
import { errorStatus } from '../../lib/httpError';
import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import GenderBadge from '../../components/shared/GenderBadge';
import EmptyState from '../../components/shared/EmptyState';
import { XMarkIcon, ChevronRightIcon, ChevronDownIcon } from '../../components/shared/Icons';
import BracketTree from '../../components/tournament/BracketTree';
import type { BracketBout, BracketSeedEntry } from '../../components/tournament/BracketTree';
import type { DrawStep } from '../../utils/bracketDraw';
import { personName } from '../../utils/personName';
import { boutScoreState, managesUnlocked } from '../../utils/tournamentState';
import type { ManualBoutRow } from '../../components/tournament/ManualBoutAssignModal';
import { successText } from '../../i18n/labels';
import { logBackgroundError } from '../../observability';
import { BADGE_XXS } from '../../components/shared/badgeSize';
import type {
  Tournament, Bout, Bracket, Ring, BoutPlanView,
} from '../../types';

/** Shape returned by GET /api/brackets/:id — the base bracket row with
 *  seeds + bouts joined. The api-client types it as `Bracket`, but the
 *  endpoint actually returns the richer detail shape (seeds + bouts). */
interface BracketDetail extends Bracket {
  seeds?: BracketSeedEntry[];
  bouts?: BracketBout[];
  /** The shape of a bracket with no bouts (#465); null once it has bouts. */
  plan?: DrawStep[] | null;
}

/** List row from GET /api/brackets/tournament/:id — the list endpoint
 *  augments each Bracket with a `bout_count` aggregate that the shared
 *  Bracket type doesn't carry. */
interface BracketListItem extends Bracket {
  bout_count?: number;
}

interface BracketsTabProps {
  id: string;
  tournament: Tournament;
  bouts: Bout[];
  brackets: BracketListItem[];
  /** Reads the list under the order guard of the hook (#664). It rejects when the read fails. */
  loadBrackets: () => Promise<void>;
  /** #591 — grows when a socket event or a resume changed the brackets or their bouts. */
  bracketsVersion: number;
  rings: Ring[];
  managesThisTournament: boolean;
  boutPlan: BoutPlanView | null;
  setBoutPlan: (v: BoutPlanView | null) => void;
  setShowBoutPlan: (v: boolean) => void;
  setShowPivotAnalysis: (v: boolean) => void;
  setManualBouts: (v: ManualBoutRow[] | null) => void;
  setManualRings: (v: Ring[]) => void;
  setManualDays: (v: string[]) => void;
  setShowManualAssign: (v: boolean) => void;
  reload: () => Promise<void>;
}

export default function BracketsTab({
  id, tournament, bouts, brackets, loadBrackets, bracketsVersion, rings, managesThisTournament,
  boutPlan, setBoutPlan, setShowBoutPlan, setShowPivotAnalysis,
  setManualBouts, setManualRings, setManualDays, setShowManualAssign,
  reload, }: BracketsTabProps) {
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const { t } = useI18n();
  const { fighterCount, boutCount } = useLabels();

  const [openBrackets, setOpenBrackets] = useState<Set<string>>(new Set());
  const [bracketDetails, setBracketDetails] = useState<Record<string, BracketDetail | null>>({});
  // #591 — one number for each read of a tree. An open, a refresh and the
  // print can read one bracket at one time, and only the newest answer writes.
  const detailSeq = useRef<Record<string, number>>({});
  const nextDetailSeq = (bid: string) => (detailSeq.current[bid] = (detailSeq.current[bid] ?? 0) + 1);
  // #591 — the brackets or their bouts changed: read each open tree again, and
  // drop the stored tree of each closed bracket, so the next open reads it. The
  // cache made a tree stale until a reload of the page.
  useEffect(() => {
    if (!bracketsVersion) return;
    let live = true;
    setBracketDetails(prev => Object.fromEntries(Object.entries(prev).filter(([bid]) => openBrackets.has(bid))));
    for (const bid of openBrackets) {
      const seq = nextDetailSeq(bid);
      getBracketDetail(bid)
        .then(r => { if (live && seq === detailSeq.current[bid]) setBracketDetails(prev => ({ ...prev, [bid]: r.data as BracketDetail })); })
        .catch(err => {
          // A bracket deleted since it opened answers 404: close it, with no error.
          if (errorStatus(err) === 404) {
            if (!live) return;
            setBracketDetails(prev => { const n = { ...prev }; delete n[bid]; return n; });
            setOpenBrackets(prev => { const n = new Set(prev); n.delete(bid); return n; });
            return;
          }
          logBackgroundError('BracketsTab:refresh')(err);
        });
    }
    return () => { live = false; };
    // Only a new version reads the trees again; the open set is read as it is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracketsVersion]);
  // #605, #664 — the lock of Configure bouts: a second tap generates no second plan.
  const planLoad = useBusy();
  // #605 — one write at a time: a second tap sends no copy. The ring select
  // and the delete of a bracket share one lock for each bracket.
  const bracketWrites = useBusyKeys();
  const resetting = useBusy();

  return (
    <div className="space-y-4">
      {brackets.length === 0 && (
        <EmptyState compact testId="brackets-empty" title={t.tournament.noBrackets}
          hint={managesThisTournament ? t.tournament.hintGenBrackets : undefined} />
      )}
      {brackets.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap no-print">
          {/* Generate / View Bout Plan
           *  - 'draft' brackets present (TM, unlocked): "Configure Bouts (N)" → generates plan
           *  - bout_plan_status === 'draft' (TM, unlocked): "Bout Preview" → loads existing draft
           *  - bout_plan_status === 'confirmed' (TM/Admin): "View Bout Plan" → read-only modal
           *    (modal hides drag-reorder + ring-change + Confirm button)
           */}
          {(
            (managesUnlocked(tournament, managesThisTournament) && (tournament.bout_plan_status === 'draft' || brackets.some(b => b.status === 'draft')))
            || (managesThisTournament && tournament.bout_plan_status === 'confirmed')
          ) && (
            <button
              disabled={planLoad.busy}
              onClick={() => planLoad.run(async () => {
                try {
                  if (tournament.bout_plan_status === 'draft' || tournament.bout_plan_status === 'confirmed') {
                    const { getBoutPlan } = await import('../../api/client');
                    const res = await getBoutPlan(id);
                    setBoutPlan(res.data);
                  } else {
                    const { generateBoutPlan, getBoutPlan } = await import('../../api/client');
                    const planRes = await generateBoutPlan(id);
                    // #419 — the plan exists. A failed refetch is another
                    // fact: a warning that keeps the advice, and no plan modal.
                    const read = await afterWrite({
                      source: 'BracketsTab:configure',
                      warnings: planRes.data.warnings,
                      refresh: async () => {
                        const res = await getBoutPlan(id);
                        setBoutPlan(res.data);
                        await reload();
                      },
                    });
                    if (!read) return;
                  }
                  setShowBoutPlan(true);
                } catch (err) { showError(err); }
              })}
              className="btn-primary btn-sm">
              {planLoad.busy ? t.common.loading
                : tournament.bout_plan_status === 'confirmed' ? t.tournament.viewBoutPlan
                : tournament.bout_plan_status === 'draft' ? t.tournament.boutPreview
                : `${t.tournament.configureBouts} (${brackets.filter(b => b.status === 'draft').length})`}
            </button>
          )}
          {/* Manual bout assignment */}
          {managesUnlocked(tournament, managesThisTournament) && brackets.some(b => b.status === 'draft') && bouts.length === 0 && (
            <button
              onClick={async () => {
                try {
                  const { getBracketBouts } = await import('../../api/client');
                  const res = await getBracketBouts(id);
                  // Cast: api/client declares `bouts: Bout[]` on this endpoint
                  // but the backend returns the denormalised ManualBoutRow
                  // shape (bracket_id + bout_idx, no materialised bout yet).
                  // Narrowing in api/client would cross the component layer
                  // boundary; casting here keeps the fix local.
                  setManualBouts(res.data.bouts as unknown as ManualBoutRow[]);
                  setManualRings(res.data.rings);
                  setManualDays(res.data.days);
                  setShowManualAssign(true);
                } catch (err) { showError(err); }
              }}
              className="btn-outline btn-sm">
              {t.tournament.manualAssignment}
            </button>
          )}
          <div className="flex-1" />
          {/* Print Brackets */}
          <button onClick={() => {
            const style = document.createElement('style');
            style.id = 'print-landscape';
            style.textContent = '@page { size: landscape; }';
            document.head.appendChild(style);
            const allIds = brackets.map(b => b.id);
            const toLoad = allIds.filter(bid => !bracketDetails[bid]);
            // #559 — a failed load left that bracket out of the printout with
            // no message. The print goes on; the person sees the failure.
            let loadFailure: unknown = null;
            // #591 — the trees and the open set change together: a refresh in
            // between dropped the trees of the closed brackets, and the print
            // showed empty cards.
            const loaded: Record<string, BracketDetail> = {};
            Promise.all(toLoad.map(async bid => {
              const seq = nextDetailSeq(bid);
              try { const r = await getBracketDetail(bid); if (seq === detailSeq.current[bid]) loaded[bid] = r.data as BracketDetail; }
              catch (err) { loadFailure ??= err; }
            })).then(() => {
              if (loadFailure) showError(loadFailure);
              setBracketDetails(prev => ({ ...prev, ...loaded }));
              setOpenBrackets(new Set(allIds));
              setTimeout(() => { window.print(); document.getElementById('print-landscape')?.remove(); }, 300);
            });
          }} className="btn-outline btn-sm">{t.tournament.printBrackets}</button>
          {/* Reset Bouts — also visible when bouts=0 but brackets stuck in 'scheduled'
              (recovery from one-off broken states where bouts were deleted but bracket
               status wasn't rolled back to 'draft'). */}
          {managesUnlocked(tournament, managesThisTournament) && (bouts.length > 0 || brackets.some(b => b.status === 'scheduled')) && !boutScoreState(bouts).any && (
            <button
              onClick={async () => {
                // #665 — ask before run(): the question must not hold the lock.
                if (!(await confirm({ message: t.tournament.resetConfirm, confirmLabel: t.tournament.resetBouts, destructive: true }))) return;
                await resetting.run(async () => {
                try {
                  const { resetBouts } = await import('../../api/client');
                  const res = await resetBouts(id);
                  // #419 — the reset committed. A failed refetch is a warning, not an error.
                  await afterWrite({
                    source: 'BracketsTab:reset',
                    success: successText(res.data),
                    refresh: async () => { await reload(); await loadBrackets(); },
                  });
                } catch (err) { showError(err); }
                });
              }}
              disabled={resetting.busy}
              className="btn-outline-red btn-sm">
              {t.tournament.resetBouts}
            </button>
          )}
        </div>
      )}

      {/* All brackets listed */}
      {brackets.map(b => (
        <div key={b.id} id={`bracket-${b.id}`} className="card overflow-hidden print-bracket-page scroll-mt-20">
          {/* #552 — the row wraps on a phone, and no pill breaks inside itself. */}
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              <GenderBadge gender={b.gender} division size="sm" />
              <span className="badge badge-gray text-xs whitespace-nowrap">{b.age_group}</span>
              <span className="badge badge-amber text-xs whitespace-nowrap">{b.weight_class}</span>
              <span className="badge badge-gray text-xs whitespace-nowrap">{fighterCount(b.fighter_count)}</span>
            </div>
            <span className={`badge text-xs whitespace-nowrap ${b.status === 'scheduled' ? 'badge-green' : 'badge-amber'}`}>
              {b.status === 'scheduled' ? boutCount(b.bout_count ?? 0) : t.tournament.draft}
            </span>
            {managesUnlocked(tournament, managesThisTournament) && b.status === 'draft' && (
              <select
                className="input text-[10px] py-0.5 w-auto disabled:opacity-50"
                value={b.ring_id || ''}
                disabled={bracketWrites.isBusy(b.id)}
                onChange={(e) => {
                  const ringId = e.target.value || null;
                  void bracketWrites.run(b.id, async () => {
                    try {
                      await api.patch(`/api/brackets/${b.id}`, { ring_id: ringId });
                    } catch (err) { showError(err); return; }
                    // #419 — the ring is stored. A failed refetch is a warning, not an error.
                    await afterWrite({ source: 'BracketsTab:ring', refresh: loadBrackets });
                  });
                }}>
                <option value="">{t.tournament.ringAuto}</option>
                {rings.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
            {b.ring_id && b.status === 'scheduled' && (
              <span className={`badge badge-gray ${BADGE_XXS} whitespace-nowrap`}>
                {rings.find(r => r.id === b.ring_id)?.name || 'Ring'}
              </span>
            )}
            {managesUnlocked(tournament, managesThisTournament) && b.status === 'draft' && (
              <button onClick={async () => {
                // #665 — ask before run(): the question must not hold the lock.
                if (!(await confirm({ message: t.tournament.deleteBracketConfirm, confirmLabel: t.common.delete, destructive: true }))) return;
                await bracketWrites.run(b.id, async () => {
                  try {
                    await deleteBracket(b.id);
                  } catch (err) { showError(err); return; }
                  setOpenBrackets(prev => { const n = new Set(prev); n.delete(b.id); return n; });
                  setBracketDetails(prev => { const n = { ...prev }; delete n[b.id]; return n; });
                  // #419 — the bracket is deleted. A failed refetch is a warning, not an error.
                  await afterWrite({ source: 'BracketsTab:delete', success: t.tournament.bracketDeleted, refresh: loadBrackets });
                });
              }} disabled={bracketWrites.isBusy(b.id)} className="btn-outline-red btn-sm"><XMarkIcon className="w-3 h-3" /></button>
            )}
          </div>
          <button data-testid="bracket-toggle" className="btn-ghost btn-sm w-full justify-start text-gray-500 inline-flex items-center gap-1"
            onClick={async () => {
              if (openBrackets.has(b.id)) {
                setOpenBrackets(prev => { const n = new Set(prev); n.delete(b.id); return n; });
                return;
              }
              setOpenBrackets(prev => new Set(prev).add(b.id));
              if (!bracketDetails[b.id]) {
                const seq = nextDetailSeq(b.id);
                try { setBracketDetails(prev => ({ ...prev, [b.id]: null })); const res = await getBracketDetail(b.id); if (seq === detailSeq.current[b.id]) setBracketDetails(prev => ({ ...prev, [b.id]: res.data as BracketDetail })); }
                catch (err) {
                  // #559 — the bracket stayed empty with no message. Close it,
                  // so a second click loads it again.
                  setBracketDetails(prev => { const n = { ...prev }; delete n[b.id]; return n; });
                  setOpenBrackets(prev => { const n = new Set(prev); n.delete(b.id); return n; });
                  showError(err);
                }
              }
            }}>
            {openBrackets.has(b.id)
              ? <><ChevronDownIcon className="w-3 h-3" /> {t.tournament.hideBracket}</>
              : <><ChevronRightIcon className="w-3 h-3" /> {t.tournament.showBracket}</>}
          </button>
          {openBrackets.has(b.id) && bracketDetails[b.id] && (() => {
            const detail = bracketDetails[b.id]!;
            return (
            <div className="px-4 pb-4">
              {detail.seeds && detail.seeds.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{t.tournament.seeds}</div>
                  <div className="flex flex-wrap gap-1">
                    {detail.seeds.map(s => (
                      <span key={s.seed_number} className="text-xs bg-gray-50 border border-gray-200 rounded-sm px-1.5 py-0.5">
                        {s.seed_number}. {personName(s.first_name, s.last_name)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <BracketTree bouts={detail.bouts ?? []} plan={detail.plan} seats={detail.seeds ?? []} fighterCount={detail.fighter_count} />
            </div>
            );
          })()}
        </div>
      ))}
    </div>
  );
}
